import React, { useState } from 'react';
import {
  Box,
  Button,
  Popover,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  Select,
} from '@mui/material';
import {
  FilterList,
  Close,
  Search,
  Sort,
  Group as GroupIcon,
} from '@mui/icons-material';
import { TaskStatus, TaskPriority, TaskType } from '../../types/common';
import './FilterPanel.scss';

export interface FilterOptions {
  status: TaskStatus[];
  priority: TaskPriority[];
  type: TaskType[];
  assignees: string[];
  epics: string[];
  sprints: string[];
  search: string;
}

interface FilterPanelProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  sortBy: string;
  onSortChange: (sortBy: string) => void;
  groupBy: 'sprint' | 'epic' | 'status' | 'none';
  onGroupByChange: (groupBy: 'sprint' | 'epic' | 'status' | 'none') => void;
  availableEpics?: Array<{ id: string; name: string }>;
  availableSprints?: Array<{ id: string; name: string }>;
  availableAssignees?: Array<{ id: string; name: string }>;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange,
  sortBy,
  onSortChange,
  groupBy,
  onGroupByChange,
  availableEpics = [],
  availableSprints = [],
  availableAssignees = [],
}) => {
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [groupAnchor, setGroupAnchor] = useState<null | HTMLElement>(null);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchor(event.currentTarget);
  };

  const handleGroupClick = (event: React.MouseEvent<HTMLElement>) => {
    setGroupAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleSortClose = () => {
    setSortAnchor(null);
  };

  const handleGroupClose = () => {
    setGroupAnchor(null);
  };

  const toggleStatus = (status: TaskStatus) => {
    const newStatus = filters.status.includes(status)
      ? filters.status.filter((s) => s !== status)
      : [...filters.status, status];
    onFilterChange({ ...filters, status: newStatus });
  };

  const togglePriority = (priority: TaskPriority) => {
    const newPriority = filters.priority.includes(priority)
      ? filters.priority.filter((p) => p !== priority)
      : [...filters.priority, priority];
    onFilterChange({ ...filters, priority: newPriority });
  };

  const toggleType = (type: TaskType) => {
    const newType = filters.type.includes(type)
      ? filters.type.filter((t) => t !== type)
      : [...filters.type, type];
    onFilterChange({ ...filters, type: newType });
  };

  const clearFilters = () => {
    onFilterChange({
      status: [],
      priority: [],
      type: [],
      assignees: [],
      epics: [],
      sprints: [],
      search: '',
    });
  };

  const getActiveFilterCount = () => {
    return (
      filters.status.length +
      filters.priority.length +
      filters.type.length +
      filters.assignees.length +
      filters.epics.length +
      filters.sprints.length +
      (filters.search ? 1 : 0)
    );
  };

  const sortOptions = [
    { value: 'title-asc', label: 'Title (A-Z)' },
    { value: 'title-desc', label: 'Title (Z-A)' },
    { value: 'priority-asc', label: 'Priority (Low to High)' },
    { value: 'priority-desc', label: 'Priority (High to Low)' },
    { value: 'status-asc', label: 'Status (A-Z)' },
    { value: 'dueDate-asc', label: 'Due Date (Earliest)' },
    { value: 'dueDate-desc', label: 'Due Date (Latest)' },
    { value: 'storyPoints-asc', label: 'Story Points (Low to High)' },
    { value: 'storyPoints-desc', label: 'Story Points (High to Low)' },
    { value: 'createdAt-desc', label: 'Newest First' },
    { value: 'createdAt-asc', label: 'Oldest First' },
  ];

  const groupOptions = [
    { value: 'sprint', label: 'Group by Sprint' },
    { value: 'epic', label: 'Group by Epic' },
    { value: 'status', label: 'Group by Status' },
    { value: 'none', label: 'No Grouping' },
  ];

  return (
    <Box className="filter-panel">
      {/* Search */}
      <TextField
        size="small"
        placeholder="Search tasks..."
        value={filters.search}
        onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
        InputProps={{
          startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 250 }}
      />

      {/* Filter Button */}
      <Button
        variant={getActiveFilterCount() > 0 ? 'contained' : 'outlined'}
        startIcon={<FilterList />}
        onClick={handleFilterClick}
        endIcon={
          getActiveFilterCount() > 0 ? (
            <Chip label={getActiveFilterCount()} size="small" color="primary" />
          ) : null
        }
      >
        Filter
      </Button>

      {/* Sort Button */}
      <Button variant="outlined" startIcon={<Sort />} onClick={handleSortClick}>
        Sort
      </Button>

      {/* Group By Button */}
      <Button variant="outlined" startIcon={<GroupIcon />} onClick={handleGroupClick}>
        Group: {groupBy === 'none' ? 'None' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
      </Button>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className="filter-panel__popover">
          <Box className="filter-panel__header">
            <Typography variant="h6">Filters</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={clearFilters}>
                Clear All
              </Button>
              <IconButton size="small" onClick={handleFilterClose}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Divider />

          {/* Status Filter */}
          <FormControl component="fieldset" sx={{ p: 2 }}>
            <FormLabel component="legend">Status</FormLabel>
            <FormGroup>
              {Object.values(TaskStatus).map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={filters.status.includes(status)}
                      onChange={() => toggleStatus(status)}
                    />
                  }
                  label={status.replace('_', ' ').toUpperCase()}
                />
              ))}
            </FormGroup>
          </FormControl>

          <Divider />

          {/* Priority Filter */}
          <FormControl component="fieldset" sx={{ p: 2 }}>
            <FormLabel component="legend">Priority</FormLabel>
            <FormGroup>
              {Object.values(TaskPriority).map((priority) => (
                <FormControlLabel
                  key={priority}
                  control={
                    <Checkbox
                      checked={filters.priority.includes(priority)}
                      onChange={() => togglePriority(priority)}
                    />
                  }
                  label={priority.toUpperCase()}
                />
              ))}
            </FormGroup>
          </FormControl>

          <Divider />

          {/* Type Filter */}
          <FormControl component="fieldset" sx={{ p: 2 }}>
            <FormLabel component="legend">Type</FormLabel>
            <FormGroup>
              {Object.values(TaskType).map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={filters.type.includes(type)}
                      onChange={() => toggleType(type)}
                    />
                  }
                  label={type.toUpperCase()}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>
      </Popover>

      {/* Sort Popover */}
      <Popover
        open={Boolean(sortAnchor)}
        anchorEl={sortAnchor}
        onClose={handleSortClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className="filter-panel__popover" sx={{ minWidth: 250, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Sort By
          </Typography>
          <Select fullWidth value={sortBy} onChange={(e) => onSortChange(e.target.value)}>
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Popover>

      {/* Group By Popover */}
      <Popover
        open={Boolean(groupAnchor)}
        anchorEl={groupAnchor}
        onClose={handleGroupClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className="filter-panel__popover" sx={{ minWidth: 200, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Group By
          </Typography>
          <FormControl component="fieldset">
            <FormGroup>
              {groupOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={groupBy === option.value}
                      onChange={() => {
                        onGroupByChange(option.value as any);
                        handleGroupClose();
                      }}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>
      </Popover>
    </Box>
  );
};

export default FilterPanel;
